import { useState, useEffect } from 'react';

// Types pour la gestion des configurations
export interface ConfigInfo {
  name: string;
  displayName: string;
  date: string;
  isDefault?: boolean;
}

export interface SaveConfigOptions {
  name?: string;
  displayName?: string;
  makeDefault?: boolean;
}

/**
 * Check if running in Tauri environment
 * Uses Tauri v2 reliable detection methods
 */
function isTauriEnvironment(): boolean {
  if (typeof window === 'undefined') return false;

  // Method 1: Check for __TAURI_IPC__ - the most reliable indicator for Tauri
  // This is present even during `tauri dev` mode
  if ((window as any).__TAURI_IPC__ !== undefined) return true;

  // Method 2: Check for window.__TAURI__ (sometimes available)
  if ((window as any).__TAURI__) return true;

  // Method 3: Check navigator userAgent for 'Tauri'
  if (typeof navigator !== 'undefined' && navigator.userAgent.includes('Tauri')) return true;

  return false;
}

// Hook pour utiliser les fonctionnalités Tauri
export default function useTauri() {
  const [isTauriApp, setIsTauriApp] = useState<boolean>(false);
  const [savedConfigs, setSavedConfigs] = useState<ConfigInfo[]>([]);

  // Vérifier si on est dans une fenêtre externe Tauri
  const checkIfExternalWindow = async (): Promise<boolean> => {
    try {
      // Vérifier via l'URL
      if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('forceExternal') === 'true') {
          return true;
        }
      }

      // Vérifier via le label de la fenêtre Tauri
      try {
        const { getCurrentWebviewWindow } = await import('@tauri-apps/api/window');
        const currentWindow = getCurrentWebviewWindow();
        if (currentWindow) {
          const windowLabel = currentWindow.label();
          // Les fenêtres externes ont un label qui commence par "external_"
          return windowLabel.startsWith('external_');
        }
      } catch (error) {
        // Si on ne peut pas accéder à l'API, on assume que ce n'est pas une fenêtre externe
        return false;
      }
    } catch (error) {
      return false;
    }
    return false;
  };

  // Charger la liste des configurations pour Tauri
  const loadConfigListForTauri = async () => {
    try {
      // Dynamic import for Tauri APIs
      const { readTextFile, exists, mkdir, BaseDirectory } = await import('@tauri-apps/plugin-fs');

      // Créer le dossier configs s'il n'existe pas
      const configDir = 'configs';
      const dirExists = await exists(configDir, { baseDir: BaseDirectory.AppData });

      if (!dirExists) {
        // Créer le dossier s'il n'existe pas
        try {
          await mkdir(configDir, { baseDir: BaseDirectory.AppData, recursive: true });
        } catch (createError) {
          // console.warn('Impossible de créer le dossier configs lors du chargement:', createError);
          setSavedConfigs([]);
          return;
        }
      }

      // Lire le fichier de liste des configs
      const listPath = `${configDir}/config-list.json`;
      const listExists = await exists(listPath, { baseDir: BaseDirectory.AppData });

      if (listExists) {
        const content = await readTextFile(listPath, { baseDir: BaseDirectory.AppData });
        setSavedConfigs(JSON.parse(content));
      } else {
        setSavedConfigs([]);
      }
    } catch (error) {
      // console.error('Erreur lors du chargement de la liste des configurations:', error);
      setSavedConfigs([]);
    }
  };

  // Charger la liste des configurations pour navigateur web
  const loadConfigListForWeb = () => {
    try {
      const configList = localStorage.getItem('visual-grid-config-list');
      if (configList) {
        setSavedConfigs(JSON.parse(configList));
      } else {
        setSavedConfigs([]);
      }
    } catch (error) {
      // console.error('Erreur lors du chargement de la liste des configurations:', error);
      setSavedConfigs([]);
    }
  };

  useEffect(() => {
    // Vérifier si l'application est exécutée dans Tauri
    const checkIsTauri = async () => {
      if (isTauriEnvironment()) {
        setIsTauriApp(true);
        
        // Vérifier si on est dans une fenêtre externe (qui n'a pas les permissions)
        const isExternalWindow = await checkIfExternalWindow();
        
        // Load configs only if not in external window
        if (!isExternalWindow) {
          await loadConfigListForTauri();
        } else {
          // En mode fenêtre externe, on ne charge pas les configs (pas de permissions)
          setSavedConfigs([]);
        }
      } else {
        setIsTauriApp(false);
        loadConfigListForWeb();
      }
    };

    checkIsTauri();
  }, []);

  // Charger la liste des configurations disponibles (utilisé pour rechargement manuel)
  const loadConfigList = async () => {
    if (isTauriApp) {
      // Vérifier si on est dans une fenêtre externe avant de charger
      const isExternalWindow = await checkIfExternalWindow();
      if (!isExternalWindow) {
        await loadConfigListForTauri();
      } else {
        // En mode fenêtre externe, on ne charge pas les configs (pas de permissions)
        setSavedConfigs([]);
      }
    } else {
      loadConfigListForWeb();
    }
  };

  // Fonction pour activer/désactiver le mode plein écran
  const toggleFullscreen = () => {
    // Utiliser toujours l'API Fullscreen du navigateur
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      document.documentElement.requestFullscreen();
    }
  };

  // Fonction pour sauvegarder une configuration
  const saveConfig = async (config: any, options: SaveConfigOptions = {}) => {
    const { name = 'default', displayName = 'Configuration par défaut', makeDefault = false } = options;
    const configName = name.replace(/[^a-z0-9-_]/gi, '_').toLowerCase();

    if (isTauriApp) {
      try {
        // Dynamic import for Tauri APIs
        const { writeTextFile, exists, mkdir, BaseDirectory } = await import('@tauri-apps/plugin-fs');
        
        const configDir = 'configs';

        // Créer le dossier configs s'il n'existe pas
        // On essaie toujours de créer le dossier (createDir avec recursive: true est idempotent)
        try {
          await mkdir(configDir, { baseDir: BaseDirectory.AppData, recursive: true });
        } catch (createError) {
          // Si le dossier existe déjà, ce n'est pas une erreur
          const dirExists = await exists(configDir, { baseDir: BaseDirectory.AppData });
          if (!dirExists) {
            // console.error('Erreur lors de la création du dossier configs:', createError);
            throw new Error(`Impossible de créer le dossier configs: ${createError instanceof Error ? createError.message : String(createError)}`);
          }
        }

        // Sauvegarder la configuration
        const configPath = `${configDir}/${configName}.json`;
        
        // Vérifier que la config est sérialisable
        let configContent: string;
        try {
          configContent = JSON.stringify(config, null, 2);
        } catch (jsonError) {
          throw new Error(`Erreur de sérialisation JSON: ${jsonError instanceof Error ? jsonError.message : String(jsonError)}`);
        }
        
        await writeTextFile(configPath, configContent, {
          baseDir: BaseDirectory.AppData
        });

        // Mettre à jour la liste des configurations
        const configInfo: ConfigInfo = {
          name: configName,
          displayName: displayName,
          date: new Date().toISOString(),
          isDefault: makeDefault
        };

        let configList = savedConfigs.slice();
        const existingIndex = configList.findIndex(c => c.name === configName);

        if (existingIndex >= 0) {
          configList[existingIndex] = {
            ...configList[existingIndex],
            displayName: displayName, // Mettre à jour le displayName
            date: configInfo.date,
            isDefault: makeDefault
          };
        } else {
          configList.push(configInfo);
        }

        // Si cette configuration est définie comme par défaut, mettre à jour les autres
        if (makeDefault) {
          configList = configList.map(c => ({
            ...c,
            isDefault: c.name === configName
          }));
        }

        // Sauvegarder la liste mise à jour
        const listPath = `${configDir}/config-list.json`;
        await writeTextFile(listPath, JSON.stringify(configList, null, 2), {
          baseDir: BaseDirectory.AppData
        });

        setSavedConfigs(configList);
        return { success: true };
      } catch (error) {
        // console.error('Erreur détaillée lors de la sauvegarde Tauri:', error);
        const errorMessage = error instanceof Error ? error.message : String(error);
        return {
          success: false,
          error: `Erreur lors de la sauvegarde: ${errorMessage}`
        };
      }
    } else {
      // Fallback pour navigateur web - utiliser localStorage
      try {
        localStorage.setItem(`visual-grid-config-${configName}`, JSON.stringify(config));

        const configInfo: ConfigInfo = {
          name: configName,
          displayName: displayName,
          date: new Date().toISOString(),
          isDefault: makeDefault
        };

        let configList = savedConfigs.slice();
        const existingIndex = configList.findIndex(c => c.name === configName);

        if (existingIndex >= 0) {
          configList[existingIndex] = {
            ...configList[existingIndex],
            date: configInfo.date,
            isDefault: makeDefault
          };
        } else {
          configList.push(configInfo);
        }

        if (makeDefault) {
          configList = configList.map(c => ({
            ...c,
            isDefault: c.name === configName
          }));
        }

        localStorage.setItem('visual-grid-config-list', JSON.stringify(configList));
        setSavedConfigs(configList);

        return { success: true };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Erreur lors de la sauvegarde'
        };
      }
    }
  };

  // Fonction pour charger une configuration
  const loadConfig = async (name: string = 'default') => {
    if (isTauriApp) {
      try {
        // Dynamic import for Tauri APIs
        const { readTextFile, exists, BaseDirectory } = await import('@tauri-apps/plugin-fs');
        
        const configDir = 'configs';
        const configPath = `${configDir}/${name}.json`;

        const fileExists = await exists(configPath, { baseDir: BaseDirectory.AppData });

        if (!fileExists && name !== 'default') {
          // Essayer de charger la configuration par défaut
          const defaultConfig = savedConfigs.find(c => c.isDefault);
          if (defaultConfig) {
            const defaultPath = `${configDir}/${defaultConfig.name}.json`;
            const content = await readTextFile(defaultPath, { baseDir: BaseDirectory.AppData });
            return { success: true, config: JSON.parse(content) };
          }
        } else if (fileExists) {
          const content = await readTextFile(configPath, { baseDir: BaseDirectory.AppData });
          return { success: true, config: JSON.parse(content) };
        }

        return { success: false, error: 'Aucune configuration trouvée' };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Erreur lors du chargement'
        };
      }
    } else {
      // Fallback pour navigateur web - utiliser localStorage
      try {
        let savedConfig = localStorage.getItem(`visual-grid-config-${name}`);

        if (!savedConfig && name !== 'default') {
          const defaultConfig = savedConfigs.find(c => c.isDefault);
          if (defaultConfig) {
            savedConfig = localStorage.getItem(`visual-grid-config-${defaultConfig.name}`);
          }
        }

        if (savedConfig) {
          return { success: true, config: JSON.parse(savedConfig) };
        }

        return { success: false, error: 'Aucune configuration trouvée' };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Erreur lors du chargement'
        };
      }
    }
  };

  // Fonction pour supprimer une configuration
  const deleteConfig = async (name: string) => {
    if (isTauriApp) {
      try {
        // Dynamic import for Tauri APIs
        const { remove, writeTextFile, BaseDirectory } = await import('@tauri-apps/plugin-fs');
        
        const configDir = 'configs';
        const configPath = `${configDir}/${name}.json`;

        // Supprimer le fichier de configuration
        await remove(configPath, { baseDir: BaseDirectory.AppData });

        // Mettre à jour la liste des configurations
        const configList = savedConfigs.filter(c => c.name !== name);
        const listPath = `${configDir}/config-list.json`;
        await writeTextFile(listPath, JSON.stringify(configList, null, 2), {
          baseDir: BaseDirectory.AppData
        });

        setSavedConfigs(configList);
        return { success: true };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Erreur lors de la suppression'
        };
      }
    } else {
      // Fallback pour navigateur web - utiliser localStorage
      try {
        localStorage.removeItem(`visual-grid-config-${name}`);

        const configList = savedConfigs.filter(c => c.name !== name);
        localStorage.setItem('visual-grid-config-list', JSON.stringify(configList));
        setSavedConfigs(configList);

        return { success: true };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Erreur lors de la suppression'
        };
      }
    }
  };

  // Fonction pour exporter une configuration sous forme de fichier .visualgrid
  const exportConfigFile = async (config: any, filename: string) => {
    if (isTauriApp) {
      try {
        // Dynamic import for Tauri APIs
        const { save } = await import('@tauri-apps/plugin-dialog');
        const { writeTextFile } = await import('@tauri-apps/plugin-fs');
        
        const filePath = await save({
          filters: [{
            name: 'VisualGrid Configuration',
            extensions: ['visualgrid']
          }],
          defaultPath: filename || 'config.visualgrid'
        });

        if (filePath) {
          // Vérifier que la config est sérialisable
          let configContent: string;
          try {
            configContent = JSON.stringify(config, null, 2);
          } catch (jsonError) {
            throw new Error(`Erreur de sérialisation JSON: ${jsonError instanceof Error ? jsonError.message : String(jsonError)}`);
          }
          
          // writeTextFile avec un chemin absolu (retourné par save dialog)
          // Note: pas besoin de BaseDirectory car filePath est déjà absolu
          await writeTextFile(filePath, configContent);
          return { success: true, path: filePath };
        }

        return { success: false, error: 'Aucun fichier sélectionné' };
      } catch (error) {
        // console.error('Erreur détaillée lors de l\'exportation Tauri:', error);
        const errorMessage = error instanceof Error ? error.message : String(error);
        return {
          success: false,
          error: `Erreur lors de l'exportation du fichier: ${errorMessage}`
        };
      }
    } else {
      // Fallback pour navigateur web
      try {
        const configBlob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(configBlob);

        const link = document.createElement('a');
        link.href = url;
        link.download = filename || 'config.visualgrid';
        link.click();

        setTimeout(() => URL.revokeObjectURL(url), 100);

        return { success: true };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Erreur lors de l\'exportation du fichier'
        };
      }
    }
  };

  // Fonction pour importer une configuration depuis un fichier
  // fileType: 'visualgrid' pour page Grid, 'mgrd' pour page MultiGrid
  const importConfigFile = async (fileType: 'visualgrid' | 'mgrd' = 'visualgrid') => {
    const extension = fileType;
    const filterName = fileType === 'visualgrid' ? 'VisualGrid Configuration' : 'MultiGrid Configuration';
    const errorMessage = fileType === 'visualgrid' 
      ? 'Seuls les fichiers .visualgrid sont acceptés' 
      : 'Seuls les fichiers .mgrd sont acceptés';

    if (isTauriApp) {
      try {
        // Dynamic import for Tauri APIs
        const { open } = await import('@tauri-apps/plugin-dialog');
        const { readTextFile } = await import('@tauri-apps/plugin-fs');
        
        const filePath = await open({
          multiple: false,
          filters: [{
            name: filterName,
            extensions: [extension]
          }]
        });

        if (filePath && typeof filePath === 'string') {
          // Vérifier que le fichier a bien l'extension correcte
          const lowerPath = filePath.toLowerCase();
          if (!lowerPath.endsWith(`.${extension}`)) {
            return { success: false, error: errorMessage };
          }
          
          const content = await readTextFile(filePath);
          const config = JSON.parse(content);
          // Extraire le nom du fichier depuis le chemin (compatible Windows/Unix)
          const fileName = filePath.split(/[/\\]/).pop() || `config.${extension}`;
          return { success: true, config, fileName };
        }

        return { success: false, error: 'Aucun fichier sélectionné' };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Erreur lors de l\'importation du fichier'
        };
      }
    } else {
      // Fallback pour navigateur web
      return new Promise<{success: boolean; config?: any; fileName?: string; error?: string}>((resolve) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = `.${extension}`;
        
        input.onchange = (event) => {
          const file = (event.target as HTMLInputElement).files?.[0];
          if (!file) {
            resolve({ success: false, error: 'Aucun fichier sélectionné' });
            return;
          }
          
          // Vérifier que le fichier a bien l'extension correcte
          const lowerName = file.name.toLowerCase();
          if (!lowerName.endsWith(`.${extension}`)) {
            resolve({ success: false, error: errorMessage });
            return;
          }

          const reader = new FileReader();
          reader.onload = (e) => {
            try {
              const content = e.target?.result as string;
              const config = JSON.parse(content);
              resolve({ success: true, config, fileName: file.name });
            } catch (error) {
              resolve({
                success: false,
                error: error instanceof Error ? error.message : 'Erreur lors de la lecture du fichier'
              });
            }
          };

          reader.onerror = () => {
            resolve({ success: false, error: 'Erreur lors de la lecture du fichier' });
          };

          reader.readAsText(file);
        };

        input.click();
      });
    }
  };

  // Fonction pour exporter le canvas
  const exportCanvas = async (dataUrl: string, filename: string) => {
    if (isTauriApp) {
      try {
        // Dynamic import for Tauri APIs
        const { save } = await import('@tauri-apps/plugin-dialog');
        const { writeFile } = await import('@tauri-apps/plugin-fs');
        
        const filePath = await save({
          filters: [{
            name: 'PNG Image',
            extensions: ['png']
          }],
          defaultPath: filename || 'visual-grid.png'
        });

        if (filePath) {
          // Convertir le dataURL en binaire et sauvegarder directement au chemin choisi
          const base64Data = dataUrl.replace(/^data:image\/png;base64,/, '');
          const binaryData = atob(base64Data);
          const bytes = new Uint8Array(binaryData.length);
          for (let i = 0; i < binaryData.length; i++) {
            bytes[i] = binaryData.charCodeAt(i);
          }

          // Écrire directement au chemin choisi par l'utilisateur
          await writeFile(filePath, bytes);

          return { success: true, path: filePath };
        }

        return { success: false, error: 'Aucun fichier sélectionné' };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Erreur lors de l\'exportation'
        };
      }
    } else {
      // Fallback pour navigateur web
      try {
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = filename || 'visual-grid.png';
        link.click();
        return { success: true };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Erreur lors de l\'exportation'
        };
      }
    }
  };

  // Fonctions pour la gestion multi-écran
  const getDisplays = async () => {
    if (isTauriApp) {
      try {
        // Dynamic import for Tauri APIs
        const { invoke } = await import('@tauri-apps/api/core');
        const displays = await invoke('get_displays');
        return displays;
      } catch (error) {
        // console.error('Erreur lors de la récupération des écrans:', error);
        return [];
      }
    }
    return [];
  };

  const openExternalDisplay = async (displayId: string, url: string, fullscreen: boolean = true) => {
    if (isTauriApp) {
      try {
        // Dynamic import for Tauri APIs
        const { invoke } = await import('@tauri-apps/api/core');
        const windowId = await invoke('open_external_display', {
          displayId,
          url,
          fullscreen
        });
        return { success: true, windowId };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Erreur lors de l\'ouverture de l\'affichage externe'
        };
      }
    }
    return { success: false, error: 'Non supporté en dehors de Tauri' };
  };

  const closeExternalDisplay = async (windowId: string) => {
    if (isTauriApp) {
      try {
        // Dynamic import for Tauri APIs
        const { invoke } = await import('@tauri-apps/api/core');
        const result = await invoke('close_external_display', { windowId });
        return { success: result };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Erreur lors de la fermeture de l\'affichage externe'
        };
      }
    }
    return { success: false, error: 'Non supporté en dehors de Tauri' };
  };

  const getExternalDisplayState = async (windowId: string) => {
    if (isTauriApp) {
      try {
        // Dynamic import for Tauri APIs
        const { invoke } = await import('@tauri-apps/api/core');
        const state = await invoke('get_external_display_state', { windowId });
        return state;
      } catch (error) {
        // console.error('Erreur lors de la récupération de l\'état:', error);
        return { exists: false };
      }
    }
    return { exists: false };
  };

  const toggleAllFullscreen = async () => {
    if (isTauriApp) {
      try {
        // Dynamic import for Tauri APIs
        const { invoke } = await import('@tauri-apps/api/core');
        await invoke('toggle_all_fullscreen');
        return { success: true };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Erreur lors du basculement plein écran'
        };
      }
    }
    return { success: false, error: 'Non supporté en dehors de Tauri' };
  };

  /**
   * Vérifier si le serveur local est déjà en cours d'exécution
   */
  const checkLocalServerRunning = async (port: number = 5000): Promise<boolean> => {
    try {
      const response = await fetch(`http://localhost:${port}/health`);
      const data = await response.json();
      return data.status === 'ok';
    } catch {
      return false;
    }
  };

  /**
   * Obtenir la configuration du serveur local (IP, port, etc.)
   */
  const getLocalServerConfig = async (port: number = 5000) => {
    try {
      const response = await fetch(`http://localhost:${port}/config`, {
        signal: AbortSignal.timeout(2000) // Timeout de 2 secondes
      });
      if (!response.ok) {
        return null;
      }
      const data = await response.json();
      return data;
    } catch (error) {
      // Silencieusement retourner null si le serveur n'est pas disponible
      return null;
    }
  };

  /**
   * Démarrer le serveur WebSocket local pour les sessions
   * Port par défaut: 5000 (évite le conflit avec Next.js 3000)
   */
  const startLocalServer = async (port: number = 5000) => {
    if (!isTauriApp) {
      return { success: false, error: 'Fonction disponible uniquement sur Tauri' };
    }

    try {
      // Vérifier si le serveur est déjà en cours d'exécution
      const isRunning = await checkLocalServerRunning(port);
      if (isRunning) {
        console.log(`✅ Serveur local déjà en cours d'exécution sur le port ${port}`);
        return {
          success: true,
          alreadyRunning: true,
          port,
          message: `Serveur WebSocket local déjà en écoute sur le port ${port}`
        };
      }

      // Dynamic import for Tauri APIs
      const { Command } = await import('@tauri-apps/plugin-shell');

      console.log(`🚀 Démarrage du serveur local sur port ${port}...`);

      // Utiliser un shell command au lieu de spawn direct
      // Cela permet d'utiliser 'node' depuis le PATH système
      const isWindows = navigator.userAgent.includes('Windows');

      // Sur Windows, utiliser cmd /c pour exécuter la commande
      // Sur Unix, utiliser sh -c
      const shellCmd = isWindows ? 'cmd' : 'sh';
      const shellArgs = isWindows
        ? ['/c', `set LOCAL_WS_PORT=${port} && cd /d d:\\Projets\\VisualGrid && node server\\local-ws-server.js`]
        : ['-c', `LOCAL_WS_PORT=${port} cd /d/Projets/VisualGrid && node server/local-ws-server.js`];

      console.log(`📂 Commande: ${shellCmd} ${shellArgs.join(' ')}`);

      const command = new Command(shellCmd, shellArgs);

      // Capturer la sortie
      command.stdout.on('data', (line) => {
        console.log(`[LocalServer] ${line}`);
      });

      command.stderr.on('data', (line) => {
        // console.error(`[LocalServer ERR] ${line}`);
      });

      command.on('close', (data) => {
        console.log(`[LocalServer] Processus fermé avec code: ${data.code}`);
      });

      command.on('error', (error) => {
        // console.error(`[LocalServer] Erreur processus: ${error}`);
      });

      // Lancer le serveur en arrière-plan
      const child = await command.spawn();

      console.log(`✅ Commande spawn lancée (PID: ${child.pid})`);

      // Attendre que le serveur démarre
      let attempts = 0;
      const maxAttempts = 10;
      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 500));
        const running = await checkLocalServerRunning(port);
        if (running) {
          console.log(`✅ Serveur local vérifié et opérationnel sur port ${port}`);
          return {
            success: true,
            pid: child.pid,
            port,
            message: `Serveur WebSocket local en écoute sur le port ${port}`
          };
        }
        attempts++;
      }

      console.warn(`⚠️ Impossible de vérifier le serveur après ${maxAttempts} tentatives`);
      return {
        success: true,
        pid: child.pid,
        port,
        warning: 'Serveur lancé mais non vérifié',
        message: `Serveur WebSocket démarré (PID: ${child.pid})`
      };

    } catch (error) {
      // console.error('❌ Erreur démarrage serveur local:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur inconnue'
      };
    }
  };

  /**
   * Arrêter le serveur WebSocket local
   * Envoie un message shutdown-server au serveur pour qu'il s'arrête proprement
   */
  /**
   * Ouvrir une URL dans le navigateur par défaut du système
   * Utilise l'API shell de Tauri pour ouvrir dans le navigateur externe
   */
  const openInBrowser = async (url: string) => {
    if (isTauriApp) {
      try {
        const { open } = await import('@tauri-apps/plugin-shell');
        await open(url);
        return { success: true };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Erreur lors de l\'ouverture du navigateur'
        };
      }
    } else {
      // Fallback pour navigateur web - ouvrir dans un nouvel onglet
      window.open(url, '_blank');
      return { success: true };
    }
  };

  const stopLocalServer = async (port: number = 5000) => {
    try {
      console.log(`🛑 Arrêt du serveur local sur port ${port}...`);

      // Vérifier si le serveur est en cours d'exécution
      const isRunning = await checkLocalServerRunning(port);
      if (!isRunning) {
        console.log(`⚠️ Serveur local n'est pas en cours d'exécution sur le port ${port}`);
        return {
          success: true,
          message: 'Serveur déjà arrêté'
        };
      }

      // Se connecter au serveur WebSocket pour envoyer le message shutdown
      const ws = new WebSocket(`ws://localhost:${port}/ws`);

      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          ws.close();
          reject(new Error('Timeout lors de l\'arrêt du serveur'));
        }, 5000);

        ws.onopen = () => {
          console.log('📤 Envoi du message shutdown-server...');
          // Envoyer le message shutdown
          ws.send(JSON.stringify({
            type: 'shutdown-server'
          }));
        };

        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            if (message.type === 'shutdown-confirmed') {
              clearTimeout(timeout);
              console.log('✅ Serveur local arrêté avec succès');
              ws.close();
              resolve({
                success: true,
                message: 'Serveur local arrêté'
              });
            }
          } catch (error) {
            // console.error('Erreur parsing message:', error);
          }
        };

        ws.onerror = (error) => {
          clearTimeout(timeout);
          // console.error('❌ Erreur lors de l\'arrêt du serveur:', error);
          reject(error);
        };

        ws.onclose = () => {
          clearTimeout(timeout);
        };
      });

    } catch (error) {
      // console.error('❌ Erreur arrêt serveur local:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur inconnue'
      };
    }
  };

  return {
    isTauriApp,
    isElectronApp: false, // Pour compatibilité
    toggleFullscreen,
    saveConfig,
    loadConfig,
    exportCanvas,
    deleteConfig,
    savedConfigs,
    loadConfigList,
    exportConfigFile,
    importConfigFile,
    // Fonctions multi-écran
    getDisplays,
    openExternalDisplay,
    closeExternalDisplay,
    getExternalDisplayState,
    toggleAllFullscreen,
    // Fonctions sessions locales
    startLocalServer,
    stopLocalServer,
    getLocalServerConfig,
    // Fonction pour ouvrir dans le navigateur externe
    openInBrowser
  };
}
